import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Key, Trash } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_INFO = {
  admin: { label: "Admin", desc: "Full access incl. users, billing & deletes", cls: "bg-primary/10 text-primary border-primary/30" },
  doctor: { label: "Doctor", desc: "Consultations, prescriptions, inventory & clinical reports", cls: "bg-primary/10 text-primary border-primary/30" },
  staff: { label: "Staff", desc: "Reception: patients, appointments, billing & stock usage", cls: "bg-muted text-muted-foreground border-border" },
  subadmin: { label: "Doctor", desc: "Legacy role — treated as Doctor", cls: "bg-primary/10 text-primary border-primary/30" },
  patient: { label: "Patient", desc: "Portal access to their own records", cls: "bg-secondary text-secondary-foreground border-border" },
};
const SELECTABLE = ["admin", "doctor", "staff"];
const EMPTY = { name: "", email: "", password: "", role: "staff", specialty: "" };

function RoleBadge({ role }) {
  const r = ROLE_INFO[role] || ROLE_INFO.staff;
  return <span data-testid={`role-badge-${role}`} className={`inline-flex px-2 py-0.5 rounded-sm border text-xs font-medium ${r.cls}`}>{r.label}</span>;
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success(`${toDelete.name} removed`);
      setToDelete(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) { toast.error("Name, email and a 6+ character password are required"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`${ROLE_INFO[form.role].label} account created`);
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const patch = async (u, body, msg) => {
    try { await api.patch(`/users/${u.id}`, body); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const savePw = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    await patch(pwFor, { password: pw }, "Password updated");
    setPwFor(null); setPw("");
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & roles" subtitle="Create doctor and staff accounts and control what they can do">
        <Button data-testid="add-user-button" onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-primary hover:bg-primary/90 gap-2">
          <UserPlus size={17} weight="bold" /> Add user
        </Button>
      </PageHeader>

      <div className="p-6 md:p-8 space-y-6">
        <div className="grid sm:grid-cols-3 gap-3">
          {SELECTABLE.map((k) => { const r = ROLE_INFO[k]; return (
            <div key={k} className="bg-card border border-border rounded-md p-4 flex gap-3">
              <ShieldCheck size={20} className="text-primary shrink-0 mt-0.5" />
              <div><p className="text-sm font-medium">{r.label}</p><p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p></div>
            </div>
          ); })}
        </div>

        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading users…</div> : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">User</th>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Role</th>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Sign-in</th>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Active</th>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-accent/40 transition-colors duration-150">
                      <td className="px-4 py-3">
                        <p className="font-medium">{u.name} {isMe && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {isMe || u.role === "patient" ? <RoleBadge role={u.role} /> : (
                          <div className="space-y-1">
                            <Select value={SELECTABLE.includes(u.role) ? u.role : "doctor"} onValueChange={(v) => patch(u, { role: v }, `Role changed to ${ROLE_INFO[v].label}`)}>
                              <SelectTrigger className="w-36 h-8" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {SELECTABLE.map((k) => <SelectItem key={k} value={k}>{ROLE_INFO[k].label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {(u.role === "doctor" || u.role === "subadmin") && u.specialty && <p className="text-xs text-muted-foreground">{u.specialty}</p>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground capitalize">{u.auth_provider}</td>
                      <td className="px-4 py-3">
                        <Switch checked={u.active !== false} disabled={isMe} data-testid={`active-switch-${u.id}`}
                          onCheckedChange={(v) => patch(u, { active: v }, v ? "Account activated" : "Account deactivated")} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.auth_provider === "password" && !isMe && (
                            <Button size="sm" variant="ghost" data-testid={`reset-pw-${u.id}`} onClick={() => { setPwFor(u); setPw(""); }} className="gap-1.5">
                              <Key size={14} /> Set password
                            </Button>
                          )}
                          {!isMe && (
                            <Button size="icon" variant="ghost" data-testid={`delete-user-${u.id}`} onClick={() => setToDelete(u)} title="Delete user" className="h-8 w-8 hover:text-destructive">
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add user</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Full name</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Priya Kumar" /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@company.com" /></div>
            <div className="space-y-1.5"><Label>Temporary password</Label>
              <Input type="text" data-testid="user-password-input" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SELECTABLE.map((k) => <SelectItem key={k} value={k}>{ROLE_INFO[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground leading-snug">{ROLE_INFO[form.role]?.desc}</p></div>
            {form.role === "doctor" && (
              <div className="space-y-1.5"><Label>Specialty <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input data-testid="user-specialty-input" value={form.specialty} onChange={(e) => set("specialty", e.target.value)} placeholder="e.g. Prosthetist & Orthotist" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="save-user-button" className="bg-primary hover:bg-primary/90">{busy ? "Creating…" : "Create account"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.email} will lose access immediately. Their past stock movements stay in History. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-user-confirm" onClick={remove} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(pwFor)} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent className="max-w-sm" data-testid="set-password-dialog">
          <DialogHeader><DialogTitle className="font-heading">Set new password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">For {pwFor?.name} ({pwFor?.email}). They will be signed out of existing sessions.</p>
          <Input type="text" data-testid="set-password-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (6+ characters)" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwFor(null)}>Cancel</Button>
            <Button onClick={savePw} data-testid="set-password-save" className="bg-primary hover:bg-primary/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
